'use client';

import { ReactNode } from 'react';
import { Container } from '../../components/Container/Container';
import NextImage, { StaticImageData } from 'next/image';
import mobilePhonePreview from './assets/mobilePhonePreview.png';
import desktopPhonePreview from './assets/desktopPhonePreview.png';
import { Heading } from '../../components/Heading/Heading';
import { Text } from '../../components/Text/Text';
import { QRCode } from '../../components/QRCode/QRCode';
import { AppStoreButtons } from '../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { AppRatingSection } from '../../components/AppRatingSection/AppRatingSection';
import { ExternalLink } from '../../components/Clickables/ExternalLink/ExternalLink';
import { DepthIllustration } from '../../components/DepthWaveWrapper/assets/DepthIllustration';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { useGetOsName } from '@hooks/useGetOsName';
import { getAppDownloadUrl, AppDownloadUrlSourceType } from '@domain/routes/getAppDownloadUrl';
import { trackEvent } from '@domain/analytics/trackEvent';

const isImageSource = (
  value: StaticImageData | ReactNode | undefined,
): value is StaticImageData => {
  return value !== null && typeof value === 'object' && 'src' in value;
};

export interface IShareViewProps {
  title?: string;
  description?: string;
  continueWebUrl?: string;
  sourceId?: string;
  mobileImage?: StaticImageData | ReactNode;
  desktopImage?: StaticImageData | ReactNode;
  showAppRatingSection?: boolean;
}

export const ShareView = ({
  title = 'Share title',
  description = 'Download Fishbrain to view catches, species, and local activity.',
  continueWebUrl,
  sourceId = 'ShareView',
  mobileImage = mobilePhonePreview,
  desktopImage = desktopPhonePreview,
  showAppRatingSection = true,
}: IShareViewProps) => {
  const { currentPage } = useAnalytics();

  const osName = useGetOsName();
  const appDownloadUrl = getAppDownloadUrl({
    appDownloadSourceType: AppDownloadUrlSourceType.ShareView,
    currentPageId: currentPage,
    osName,
  });

  const downloadAppClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: sourceId,
        type: 'download',
        page: currentPage,
      },
    });
  };

  const continueWebClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: sourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  return (
    <main className="relative flex min-h-dvh flex-col items-center justify-center">
      <DepthIllustration
        orientation="topLeft"
        className="absolute top-0 left-0 -z-10 hidden sm:flex"
      />
      <DepthIllustration
        orientation="bottomRight"
        className="absolute right-0 bottom-0 -z-10 hidden sm:flex"
      />
      <Container
        variant="thin"
        className="flex h-full flex-col items-center justify-start gap-6 px-4 py-6 sm:py-11 md:flex-row md:justify-center"
      >
        <div className="sm:w-col-8 md:w-col-4 w-col-3 flex w-full justify-center">
          <div className={`w-full ${desktopImage ? 'sm:hidden' : ''}`}>
            {isImageSource(mobileImage) ? (
              <NextImage
                className="w-full"
                src={mobileImage}
                alt="App Section"
                placeholder="blur"
                sizes="(min-width: 1680px) 575px,(min-width: 990px) 50vw, (min-width: 600px) 66vw, 100vw"
              />
            ) : (
              (mobileImage as ReactNode | undefined)
            )}
          </div>
          {desktopImage && (
            <div className="hidden w-full sm:flex">
              {isImageSource(desktopImage) ? (
                <NextImage
                  className="w-full"
                  src={desktopImage}
                  alt="App Section"
                  placeholder="blur"
                  sizes="(min-width: 1680px) 575px,(min-width: 990px) 50vw, (min-width: 600px) 66vw, 100vw"
                />
              ) : (
                (desktopImage as ReactNode | undefined)
              )}
            </div>
          )}
        </div>

        <div className="sm:w-col-8 md:w-col-6 flex w-full flex-col items-center justify-center gap-4 px-6 sm:gap-6 md:items-start">
          <Heading as="h1" variant="md" className="m-0 text-center md:text-left">
            {title}
          </Heading>
          <Text as="div" variant="md" className="text-center font-semibold sm:flex md:text-left">
            {description}
          </Text>
          {showAppRatingSection && (
            <AppRatingSection className="items-center justify-center gap-6 sm:flex-row sm:justify-start" />
          )}

          <div
            className={`${continueWebUrl ? 'hidden' : 'flex'} md:flex md:flex-col md:items-center md:justify-center md:gap-4`}
          >
            <div className="flex flex-row justify-center gap-4 sm:flex-wrap">
              <QRCode className="hidden justify-center sm:flex" source={sourceId} />
              <AppStoreButtons className="" orientation="vertical" source={sourceId} />
            </div>
          </div>

          {continueWebUrl && (
            <>
              <ExternalLink
                variant="buttonPrimary"
                href={appDownloadUrl}
                size="default"
                className="w-full md:hidden"
                onClick={downloadAppClick}
              >
                Download
              </ExternalLink>
              <ExternalLink
                variant="buttonSecondary"
                href={continueWebUrl}
                size="default"
                className="w-full md:w-[301px]"
                onClick={continueWebClick}
              >
                Continue on web
              </ExternalLink>
            </>
          )}
        </div>
      </Container>
    </main>
  );
};
